import React, { useEffect, useState } from 'react';
import { isPWAStandalone } from '../serviceWorkerRegistration';

/**
 * Install prompt for Lauft Pro.
 *
 * - On Android Chrome / Edge: uses the native beforeinstallprompt event
 *   to trigger the OS install dialog with a single tap.
 * - On iOS Safari: shows a custom modal explaining the Share → Add to
 *   Home Screen gesture, since iOS has no programmatic install API.
 * - Hidden if already running in PWA standalone mode, or if the agent
 *   has dismissed the prompt for this session.
 *
 * Storage key: dismissed state is kept in sessionStorage (not localStorage)
 * so the prompt comes back next session — agents who skip once still get
 * gently reminded.
 */
const STORAGE_KEY = 'lauftpro-install-dismissed';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' &&
      window.sessionStorage.getItem(STORAGE_KEY) === 'true'
  );

  useEffect(() => {
    // Already installed and running standalone — don't show
    if (isPWAStandalone()) return;
    if (dismissed) return;

    const isIOS =
      /iPhone|iPad|iPod/.test(navigator.userAgent) &&
      !window.MSStream;
    const isAndroidChrome = /Android/.test(navigator.userAgent);

    // Android: listen for the install prompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show after a short delay so it doesn't appear during initial render
      setTimeout(() => setShowAndroidPrompt(true), 2500);
    };

    if (isAndroidChrome) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    }

    // iOS: no event API exists — just check userAgent and show instructions
    // after the agent has been on the site for a few seconds (so we don't
    // interrupt their first impression).
    if (isIOS) {
      const timer = setTimeout(() => setShowIOSInstructions(true), 4000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [dismissed]);

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`[PWA] install prompt outcome: ${outcome}`);
    setDeferredPrompt(null);
    setShowAndroidPrompt(false);
    if (outcome === 'dismissed') {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowAndroidPrompt(false);
    setShowIOSInstructions(false);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {
      // sessionStorage not available — fail quietly
    }
  };

  // ===== ANDROID PROMPT =====
  if (showAndroidPrompt) {
    return (
      <div style={overlayStyle} onClick={handleDismiss}>
        <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
          <img
            src="/icons/icon-192.png"
            alt="Lauft Pro"
            style={{ width: 64, height: 64, borderRadius: 14, marginBottom: 16 }}
          />
          <h3 style={titleStyle}>Install Lauft Pro</h3>
          <p style={bodyStyle}>
            Add Lauft Pro to your home screen for quick access and instant
            notifications when new users are available.
          </p>
          <div style={buttonRowStyle}>
            <button onClick={handleDismiss} style={secondaryButtonStyle}>
              Not now
            </button>
            <button onClick={handleAndroidInstall} style={primaryButtonStyle}>
              Install
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== iOS INSTRUCTIONS =====
  if (showIOSInstructions) {
    return (
      <div style={overlayStyle} onClick={handleDismiss}>
        <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
          <img
            src="/icons/icon-192.png"
            alt="Lauft Pro"
            style={{ width: 64, height: 64, borderRadius: 14, marginBottom: 16 }}
          />
          <h3 style={titleStyle}>Add Lauft Pro to Home Screen</h3>
          <p style={bodyStyle}>
            Get the full experience with quick access and notifications.
          </p>
          <ol style={instructionListStyle}>
            <li>
              Tap the <strong>Share</strong> button{' '}
              <span style={shareIconStyle}>⎋</span> at the bottom of Safari
            </li>
            <li>
              Scroll and tap <strong>Add to Home Screen</strong>
            </li>
            <li>
              Tap <strong>Add</strong> in the top right
            </li>
          </ol>
          <button
            onClick={handleDismiss}
            style={{ ...primaryButtonStyle, width: '100%' }}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// ===== STYLES =====
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  zIndex: 100000,
  padding: 16,
  animation: 'fadeIn 200ms ease',
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: 16,
  padding: 24,
  maxWidth: 400,
  width: '100%',
  marginBottom: 16,
  boxShadow: '0 16px 48px rgba(0, 0, 0, 0.24)',
  textAlign: 'center',
  fontFamily: 'inherit',
};

const titleStyle = {
  margin: '0 0 8px 0',
  fontSize: 20,
  fontWeight: 700,
  color: '#000000',
};

const bodyStyle = {
  margin: '0 0 20px 0',
  fontSize: 14,
  lineHeight: 1.5,
  color: '#4b5563',
};

const instructionListStyle = {
  textAlign: 'left',
  margin: '0 0 20px 0',
  paddingLeft: 20,
  fontSize: 14,
  lineHeight: 1.7,
  color: '#374151',
};

const shareIconStyle = {
  display: 'inline-block',
  background: '#e5e7eb',
  padding: '0 6px',
  borderRadius: 4,
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const buttonRowStyle = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
};

const primaryButtonStyle = {
  background: '#000000',
  color: '#ffffff',
  border: 'none',
  padding: '12px 20px',
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle = {
  background: 'transparent',
  color: '#6b7280',
  border: 'none',
  padding: '12px 16px',
  borderRadius: 10,
  fontSize: 14,
  cursor: 'pointer',
};

export default PWAInstallPrompt;